import { useContext, useState } from 'react';
import { Drawer, SwipeableDrawer, Tooltip } from '@mui/material';
import { Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import sporttiaIcon from 'src/images/sporttia-icon.svg';
import { AppContext } from '../../../sporttia/all';
import generalConfig from '../../../config/general';
import { SettingsDrawer } from '../../settings/SettingsDrawer';
import { filterVisibleMenus } from './filterVisibleMenus';
import { MenuIcon } from './menuIcons';
import { menuLabel } from './menuLabel';
import { promoteFlatSectionMenus } from './promoteFlatSectionMenus';
import SidebarCollapsableItem from './SidebarCollapsableItem';
import SidebarItem from './SidebarItem';
import SidebarNotifications from './SidebarNotifications';
import SidebarQuickAccess from './SidebarQuickAccess';
import SidebarSearch from './SidebarSearch';
import SidebarUser from './SidebarUser';
import { useSidebarState } from './SidebarStateContext';
import { SIDEBAR_SECTION_LABELS, groupMenusBySection } from './sidebarSections';
import { hrefFor, useMenuNavigation } from './useMenuNavigation';
import type { Menu } from './types';

/** Ancho del sidebar. Gemelo de `--sidebar-width` en src/index.css. */
const SIDEBAR_WIDTH = 'var(--sidebar-width)';

/**
 * Sidebar del gestor. Réplica del sidebar de sporttia-competitions: superficie
 * oscura fija, cabecera con marca y accesos a notificaciones y ajustes,
 * buscador de menús, menús agrupados por sección, accesos rápidos y bloque de
 * usuario al pie.
 *
 * Lleva `id="header"` porque el tour guiado (useMenusTour) referencia
 * `#header #timetable`, `#header #notifications`, `#header #account`…
 */
export default function AppSidebar(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { open, openMobile, isMobile, toggle, closeMobile } =
        useSidebarState();
    const { go, isActive, isMenuActive } = useMenuNavigation();
    const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
    const [toggledGroups, setToggledGroups] = useState<Record<string, boolean>>(
        {},
    );

    const menus = filterVisibleMenus((cxt?.menus?.sc as Menu[]) ?? [], {
        privileges: cxt?.privileges ?? [],
        modules: cxt?.sc?.modules ?? [],
        zero: !!cxt?.zero,
        isProduction: generalConfig.environment === 'PRO',
        rolesEnabled: !!cxt?.rolesEnabled,
        permissions: cxt?.permissions ?? [],
    });
    // COMPETICIONES no agrupa: sus entradas se pintan al mismo nivel.
    const flatMenus = promoteFlatSectionMenus(menus);
    const sections = groupMenusBySection(flatMenus);

    // Un grupo está abierto si el usuario lo abrió, y si no, cuando contiene la
    // página actual. Sin efectos: se deriva del estado y de la ruta.
    const isGroupOpen = (menu: Menu): boolean =>
        toggledGroups[menu.id] ?? isMenuActive(menu);

    const toggleGroup = (menu: Menu): void =>
        setToggledGroups((previous) => ({
            ...previous,
            [menu.id]: !isGroupOpen(menu),
        }));

    const content = (
        <div
            id="header"
            data-mobile-open={isMobile ? openMobile : undefined}
            className="sporttia-sidebar bg-sidebar text-sidebar-foreground flex h-full flex-col"
        >
            <div className="flex flex-col gap-2 p-2">
                <div className="flex items-center gap-2 px-2">
                    {/* El símbolo, no el logotipo: el nombre del producto y la
                        superficie van al lado como texto, igual que en
                        sporttia-manager. El SVG trae su propio cuadrado de
                        tinta, así que no necesita fondo debajo. */}
                    <img
                        src={sporttiaIcon}
                        alt="Icono de Sporttia"
                        className="h-8 w-8 shrink-0 rounded-lg"
                    />
                    <div className="min-w-0 flex-1 leading-tight">
                        <p className="m-0 text-lg leading-tight font-semibold whitespace-nowrap text-white">
                            Sporttia
                        </p>
                        <p className="text-sidebar-foreground/70 m-0 text-xs leading-tight whitespace-nowrap">
                            {t('sidebar.subtitle')}
                        </p>
                    </div>
                    <div className="flex items-center gap-1">
                        <SidebarNotifications />
                        {/* Mismo botón que la campana (28x28, radio 6): un
                            `IconButton` de MUI queda redondo y 2px más pequeño, y
                            eso descolocaba la pareja respecto a competitions. */}
                        <Tooltip title={t('settings.title')} arrow>
                            <button
                                type="button"
                                id="tour-sidebar-settings"
                                onClick={() => setSettingsOpen(true)}
                                aria-label={t('settings.title')}
                                className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/30 flex h-7 w-7 cursor-pointer items-center justify-center rounded-md transition-colors"
                            >
                                <Settings className="h-4 w-4" />
                            </button>
                        </Tooltip>
                    </div>
                </div>

                {/* Mismo encaje que competitions: el buscador va con px-2 pt-2
                    dentro de la cabecera del sidebar. */}
                <div className="px-2 pt-2">
                    <SidebarSearch menus={flatMenus} />
                </div>
            </div>

            <nav
                id="tour-sidebar-menus"
                className="flex flex-1 flex-col gap-2 overflow-y-auto"
            >
                {sections.map(({ section, items }) => (
                    <div key={section} className="flex flex-col p-2">
                        <p className="text-sidebar-foreground/70 m-0 flex h-8 items-center px-2 text-xs font-medium tracking-wider uppercase">
                            {t(SIDEBAR_SECTION_LABELS[section])}
                        </p>
                        <ul className="flex flex-col gap-1">
                            {items.map((menu) =>
                                menu.submenus && menu.submenus.length > 0 ? (
                                    <SidebarCollapsableItem
                                        key={menu.id}
                                        menu={menu}
                                        isOpen={isGroupOpen(menu)}
                                        onToggle={() => toggleGroup(menu)}
                                    />
                                ) : (
                                    <SidebarItem
                                        key={menu.id}
                                        id={`tour-menu-${menu.id}`}
                                        caption={menuLabel(t, menu.caption)}
                                        href={hrefFor(menu)}
                                        isActive={isActive(menu)}
                                        onClick={() => go(menu)}
                                        icon={<MenuIcon id={menu.id} />}
                                        beta={menu.beta}
                                        novelty={menu.novelty}
                                    />
                                ),
                            )}
                        </ul>
                    </div>
                ))}
            </nav>

            <div className="flex flex-col gap-2 p-2">
                <SidebarQuickAccess />
                <SidebarUser />
            </div>

            <SettingsDrawer
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
            />
        </div>
    );

    if (isMobile) {
        return (
            <SwipeableDrawer
                anchor="left"
                open={openMobile}
                onOpen={toggle}
                onClose={closeMobile}
                slotProps={{
                    paper: {
                        sx: {
                            width: SIDEBAR_WIDTH,
                            maxWidth: '85vw',
                            border: 'none',
                            borderRight: '1px solid var(--sidebar-border)',
                            backgroundImage: 'none',
                        },
                    },
                }}
            >
                {content}
            </SwipeableDrawer>
        );
    }

    return (
        <Drawer
            variant="permanent"
            open={open}
            sx={{
                width: open ? SIDEBAR_WIDTH : 0,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: SIDEBAR_WIDTH,
                    border: 'none',
                    borderRight: '1px solid var(--sidebar-border)',
                    backgroundImage: 'none',
                    transform: open
                        ? 'none'
                        : `translateX(calc(-1 * ${SIDEBAR_WIDTH}))`,
                    transition: (theme) =>
                        theme.transitions.create(['transform'], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                },
            }}
        >
            {content}
        </Drawer>
    );
}
